import { FlexColumn, FlexRow } from "Components/Flex";
import { arrayRemove, arrayUnion, updateDoc } from "firebase/firestore";
import { useArtistGroupsByIdsQuery } from "hooks";
import { useArtistGroupsSearch } from "hooks/useArtistGroupsSearch";
import { AutocompleteAsync } from "melodies-source/Autocomplete";
import { Button } from "melodies-source/Button";
import { Modal } from "melodies-source/Modal";
import { SvgCheckCircle } from "melodies-source/Svgs/CheckCircle";
import { Body1 } from "melodies-source/Text";
import { addToast } from "melodies-source/Toast";
import { useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { getArtistImageById } from "Utils/getArtistImageById";
import { useArtistContext } from "Components/ArtistProvider";
import { ToastType } from "react-hot-toast";
import { Spinner } from "melodies-source/Spinner";
import { ArtistGroupOption } from "./ArtistGroupOption";
import { ArtistGroupCard } from "./ArtistGroupCard";
import { Ref } from "refs";
import { SurveyRootDoc } from "@max/common";
import { getPublishFunction } from "@max/common/dist/functions/setfan/publish";
import { getFunctions } from "firebase/functions";

interface EditAdditionalGroupsProps {
  surveyId: string;
  open: boolean;
  onClose: () => void;
  refreshTypesense: () => void;
}

const publish = getPublishFunction(getFunctions());

const SURVEY_PATH = "sts3_surveys/{surveyId}" as const;

export const EditAdditionalGroups = ({
  surveyId,
  open,
  onClose,
  refreshTypesense,
}: EditAdditionalGroupsProps) => {
  const [text, setText] = useState("");
  const [remove, setRemove] = useState<{ id: string; name: string }>();
  const [isProcessing, setIsProcessing] = useState(false);
  const { search } = useArtistGroupsSearch();
  const { logAction } = useArtistContext();

  const [survey] = useDocumentData<SurveyRootDoc, "id", "ref">(
    Ref(SURVEY_PATH, { surveyId }),
    { idField: "id", refField: "ref" },
  );

  const additionalGroups = survey?.additionalGroups ?? [];

  const { artistGroups } = useArtistGroupsByIdsQuery({
    ids: additionalGroups,
    limit: 100,
  });

  const showToast = (msg: string, variant: ToastType = "success") =>
    addToast(msg, variant, { icon: <SvgCheckCircle /> });

  const changeArtists = async (
    artist: { id: string; name: string },
    action: "add" | "remove",
  ) => {
    if (action === "add" && additionalGroups.length >= 5) {
      showToast(
        "Maximum of 5 additional groups supported. Please remove one to add another.",
        "error",
      );
      return;
    }
    setIsProcessing(true);
    const arrayOperation = action === "add" ? arrayUnion : arrayRemove;
    try {
      await updateDoc(Ref("set_fan_builder_surveys/{surveyId}", { surveyId }), {
        additionalGroups: arrayOperation(artist.id),
      });

      await logAction(`portal_${action}_artist`, {
        artistId: artist.id,
        survey: survey.id,
      });

      // this ensures the `additionalGroups` changes are propagated to the `surveys` collection in typesense
      // as well as the survey root doc. we only have perms to write to the builder doc above
      const {
        data: { success },
      } = await publish({
        surveyId,
        status: survey.status as "draft" | "live" | "completed" | "deleted",
      });
      if (!success) {
        throw new Error("`publish` failed when adding artist");
      }

      refreshTypesense();

      showToast(`${artist.name} ${action === "add" ? "added" : "removed"}`);
    } catch (err) {
      // on failure, revert adding artist to additional groups for data consistency.
      // could end up with an artist not realizing they have an artist added, who will
      // appear on the next successful publish
      await updateDoc(Ref("set_fan_builder_surveys/{surveyId}", { surveyId }), {
        additionalGroups: arrayRemove(artist.id),
      });
      console.error(err);
      showToast(`unable to ${action} ${artist.name}`, "error");
    } finally {
      setRemove(undefined);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Modal
        header="Delete Artist?"
        isOpen={!!remove}
        onClose={() => setRemove(undefined)}
      >
        <FlexColumn gap="20px">
          <Body1>
            Are you sure you want to delete {remove?.name} from this show?
          </Body1>
          <FlexRow gap="20px" xEnd>
            <Button variant="tertiary" onClick={() => setRemove(undefined)}>
              Cancel
            </Button>
            <Button
              disabled={isProcessing}
              loading={isProcessing}
              onClick={async () => await changeArtists(remove, "remove")}
            >
              Delete
            </Button>
          </FlexRow>
        </FlexColumn>
      </Modal>
      <Modal
        header="Add Artist"
        isOpen={open}
        onClose={onClose}
        withBackdropClose={!remove}
      >
        <FlexColumn gap="20px">
          <Body1>
            You can add artists to this campaign by searching below, or remove
            artists by clicking the delete icon.
          </Body1>
          <AutocompleteAsync
            label="Artist Search"
            placeholder="Type Artist Name..."
            text={text}
            setText={setText}
            customOption={ArtistGroupOption}
            clearOnSelect={true}
            onChange={async (option) =>
              await changeArtists(
                { id: option.value, name: option.label },
                "add",
              )
            }
            getOptions={async (q) => {
              const response = await search({ q, query_by: "name" });
              const documents =
                response?.hits?.map((hit) => hit.document) ?? [];

              return documents
                .filter(({ id }) => id !== survey?.artistGroupId)
                .filter(({ id }) => !survey?.additionalGroups?.includes(id))
                .map((document) => ({
                  label: document.name,
                  value: document.id,
                  avatarSrc: getArtistImageById(document.id),
                }));
            }}
          />
          {isProcessing && <Spinner />}
          <FlexColumn gap="5px">
            {artistGroups?.map((artistGroup) => (
              <ArtistGroupCard
                key={artistGroup.id}
                artistGroup={artistGroup}
                onDelete={() => setRemove(artistGroup)}
              />
            ))}
          </FlexColumn>
        </FlexColumn>
      </Modal>
    </>
  );
};
